<template>
  <div v-if="showSelector" :class="wrapperClass">
    <QSelect
      :model-value="currentLang"
      :borderless="borderless"
      :dense="dense"
      :dark="dark"
      :dropdown-icon="dropdownIcon"
      :options="langs"
      option-value="code"
      :option-label="optionLabel"
      :class="selectClass"
      :popup-content-class="popupContentClass"
      behavior="menu"
      @update:model-value="handleLanguageChange"
    >
      <!-- Custom selected content slot -->
      <template v-if="useCustomSelected" #selected>
        <slot name="selected" :current-lang="currentLang">
          <div :class="selectedTextClass">
            {{
              displayMode === "code"
                ? currentLang?.code.toUpperCase()
                : currentLang?.display
            }}
          </div>
        </slot>
      </template>

      <!-- Prepend slot for icon -->
      <template v-if="showIcon" #prepend>
        <slot name="prepend">
          <QIcon :name="iconName" :size="iconSize" />
        </slot>
      </template>
    </QSelect>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    // Appearance props
    displayMode?: "code" | "name" | "custom"; // How to display the language
    wrapperClass?: string; // Class for the wrapper div
    selectClass?: string; // Class for the QSelect component
    selectedTextClass?: string; // Class for the selected text
    popupContentClass?: string; // Class for the popup content

    // QSelect props
    borderless?: boolean;
    dense?: boolean;
    dark?: boolean;
    dropdownIcon?: string;
    optionLabel?: string;

    // Icon props
    showIcon?: boolean;
    iconName?: string;
    iconSize?: string;

    // Behavior props
    useCustomSelected?: boolean; // Whether to use the custom selected slot
  }>(),
  {
    displayMode: "name",
    wrapperClass: "tw-flex tw-items-center",
    selectClass: "",
    selectedTextClass: "",
    popupContentClass: "tw-cursor-pointer",

    borderless: true,
    dense: true,
    dark: false,
    dropdownIcon: "expand_more",
    optionLabel: "display",

    showIcon: true,
    iconName: "language",
    iconSize: "xs",

    useCustomSelected: false,
  },
);

const emit = defineEmits(["language-changed"]);

const langStore = useLanguageStore();
const { $i18n } = useNuxtApp();
const { setCurrentSymbol, currencies } = useCurrencyStore();
const { currentLocaleCode } = useLanguage();

const currency = computed(() =>
  currencies.find(
    (currency: Currency) => currency._id === currentLang.value?.currencyId,
  ),
);

// Set currency symbol when language is set on before mounted
onBeforeMount(() => {
  setCurrentSymbol(currency.value?.symbol);
});

const langs = computed(() => langStore.value);
const showSelector = computed(() => langs.value.length > 1);

const currentLang = computed(() => {
  const lang = langStore.value.find(
    (lang: Language) => lang.code === currentLocaleCode.value,
  );
  if (lang) {
    return {
      ...lang,
      display:
        props.displayMode === "code" ? lang.code.toUpperCase() : lang.display,
    };
  }
  return null;
});

// Handle language change by updating locale and redirecting
const handleLanguageChange = async (lang: { code?: string }) => {
  // Type safety - make sure we're working with valid language data
  if (!lang || !lang.code) {
    return;
  }

  // Use language code from the selected language option
  const langCode = lang.code as "th" | "en" | "zh";

  // Set locale through i18n and update cookie so detectBrowserLanguage
  // doesn't override it back to the previous language on refresh
  await $i18n.setLocale(langCode);
  $i18n.setLocaleCookie(langCode);
  emit("language-changed", langCode);

  // Redirect to the appropriate URL
  if (langStore.main.code !== langCode) {
    window.location.href = `/${langCode}`;
  } else {
    window.location.href = "/";
  }
};
</script>
